import { useState } from "react";
import { ChevronDown, ChevronUp, FileWarning, Receipt } from "lucide-react";
import { eurExact, monthLabel, shortDate } from "./format";
import { ImportInvoicesButton } from "./ImportInvoicesButton";

export const InvoicesPanel = ({ invoices, month, importedAt, onImported }) => {
  const [open, setOpen] = useState(true);
  const net = invoices.reduce((s, i) => s + (i.net || 0), 0);
  const vat = invoices.reduce((s, i) => s + (i.vat || 0), 0);
  const amount = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const cns = invoices.filter((i) => i.is_credit_note).length;

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-amber-500/40 bg-amber-950/20 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="invoices-cta">
        <div className="flex items-start gap-3">
          <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <div>
            <p className="font-semibold text-amber-200">No invoice report imported for {monthLabel(month)}</p>
            <p className="text-sm text-amber-200/70">The Invoiced figure above is by status change. Export PrintLogic's "Invoices Issued" report (PDF or CSV) for this month and import it for exact dates and net values.</p>
          </div>
        </div>
        <ImportInvoicesButton variant="inline" onImported={onImported} />
      </div>
    );
  }

  return (
    <div className="min-w-0 rounded-2xl bg-slate-900/70 ring-1 ring-white/5" data-testid="invoices-panel">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 p-5 text-left" data-testid="invoices-panel-toggle">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Invoices issued · {monthLabel(month)}</p>
          <p className="mt-0.5 text-sm text-slate-500">From PrintLogic invoice report{importedAt ? ` · imported ${shortDate(importedAt)}` : ""} · {invoices.length - cns} invoice{invoices.length - cns === 1 ? "" : "s"}{cns ? ` · ${cns} credit note${cns === 1 ? "" : "s"}` : ""}</p>
        </div>
        <span className="flex items-center gap-3">
          <span className="font-mono text-lg font-bold text-emerald-300" data-testid="invoices-panel-net">{eurExact(net)}</span>
          {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-white/5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                <th className="px-5 py-3">Inv #</th>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Customer</th>
                <th className="px-3 py-3">Order</th>
                <th className="px-3 py-3">Rep</th>
                <th className="px-3 py-3 text-right">Net</th>
                <th className="hidden px-3 py-3 text-right sm:table-cell">VAT</th>
                <th className="hidden px-5 py-3 text-right sm:table-cell">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {invoices.map((i) => (
                <tr key={i.invoice_number} className={`hover:bg-white/[0.03] ${i.is_credit_note ? "text-rose-300" : ""}`} data-testid={`invoice-row-${i.invoice_number}`}>
                  <td className="px-5 py-2.5 font-mono font-bold text-emerald-300"><Receipt className="mr-1.5 inline h-3.5 w-3.5" />{i.invoice_number}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-400">{shortDate(i.invoice_date)}</td>
                  <td className="max-w-[220px] truncate px-3 py-2.5 text-slate-100" title={i.customer}>{i.customer}</td>
                  <td className="px-3 py-2.5 font-mono text-cyan-300">{i.order_number ? `#${i.order_number}` : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2.5 font-heading text-xs font-black text-slate-300">{i.order_rep || i.cust_rep || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold">{eurExact(i.net)}</td>
                  <td className="hidden px-3 py-2.5 text-right font-mono text-slate-400 sm:table-cell">{eurExact(i.vat)}</td>
                  <td className="hidden px-5 py-2.5 text-right font-mono text-slate-300 sm:table-cell">{eurExact(i.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-white/15 bg-white/[0.03] font-bold" data-testid="invoices-total-row">
                <td className="px-5 py-3 text-[11px] uppercase tracking-[0.14em] text-slate-300" colSpan={5}>Total</td>
                <td className="px-3 py-3 text-right font-mono text-emerald-200" data-testid="invoices-total-net">{eurExact(net)}</td>
                <td className="hidden px-3 py-3 text-right font-mono text-slate-300 sm:table-cell">{eurExact(vat)}</td>
                <td className="hidden px-5 py-3 text-right font-mono text-slate-100 sm:table-cell">{eurExact(amount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
